import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import {
  fmt,
  fmtB,
  fmtDate,
  type CreatePropertyTaxItem,
  type PropertyTaxDerivedStatus,
  type PropertyTaxItem,
  type PropertyTaxType,
} from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { DepartmentSelect, DimensionFilter } from '../components/dimensions';
import { HelpButton } from '../components/help/HelpButton';
import { PageHead } from '../components/layout';
import {
  useActiveCompanyId,
  useBranches,
  useCreatePropertyTaxItem,
  usePayPropertyTaxItem,
  usePropertyTaxItems,
  usePropertyTaxSummary,
} from '../lib/queries';

// ============================================================
// Local types & static palettes
// ============================================================
type TabKey = 'items' | 'matrix' | 'summary';

interface TaxTypeMeta {
  code: PropertyTaxType;
  label: string;
  short: string;
  acct: string;
  accrued: string;
  tone: string;
  dueMonth: string;
  desc: string;
}

// 2 tax types per spec §32. Matches the COA codes wired in the API service.
const TAX_TYPES: readonly TaxTypeMeta[] = [
  {
    code: 'LAND_BUILDING',
    label: 'ภาษีที่ดินและสิ่งปลูกสร้าง',
    short: 'ที่ดิน',
    acct: '60110114',
    accrued: '20170036',
    tone: '#1a56a6',
    dueMonth: 'เม.ย.',
    desc: 'จ่ายภายในวันที่ 30 เม.ย. ของทุกปี ตาม พรบ. ภาษีที่ดินและสิ่งปลูกสร้าง 2562',
  },
  {
    code: 'SIGN',
    label: 'ภาษีป้าย',
    short: 'ป้าย',
    acct: '60110011',
    accrued: '20170035',
    tone: '#b54708',
    dueMonth: 'มี.ค.',
    desc: 'จ่ายภายในวันที่ 31 มี.ค. ของทุกปี ตามประมวลรัษฎากร',
  },
];

const MONTHS: readonly string[] = [
  'ม.ค.',
  'ก.พ.',
  'มี.ค.',
  'เม.ย.',
  'พ.ค.',
  'มิ.ย.',
  'ก.ค.',
  'ส.ค.',
  'ก.ย.',
  'ต.ค.',
  'พ.ย.',
  'ธ.ค.',
];

const SEARCH_BG =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%238a8a93' stroke-width='2'%3E%3Ccircle cx='11' cy='11' r='7'/%3E%3Cpath d='M21 21l-4.3-4.3'/%3E%3C/svg%3E\")";

function getTaxType(code: PropertyTaxType): TaxTypeMeta {
  return TAX_TYPES.find((t) => t.code === code) ?? TAX_TYPES[0]!;
}

// Wire status comes as derivedStatus (PENDING / PAID / OVERDUE / CANCELLED).
// PENDING + OVERDUE are both pre-payment; CANCELLED is terminal-no-JV.
function statusBadge(s: PropertyTaxDerivedStatus): { l: string; c: string } {
  if (s === 'PAID') return { l: 'จ่ายแล้ว', c: 'positive' };
  if (s === 'OVERDUE') return { l: 'เกินกำหนด', c: 'negative' };
  if (s === 'CANCELLED') return { l: 'ยกเลิก', c: 'neutral' };
  return { l: 'รอจ่าย', c: 'info' };
}

// Decimal-as-string -> number (display only). Server returns canonical strings;
// keep number conversion shallow so big values don't lose precision in the UI.
function toNum(v: unknown): number {
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

export const Route = createFileRoute('/proptax')({
  component: PropTaxPage,
});

function PropTaxPage() {
  const companyId = useActiveCompanyId();
  const [year, setYear] = useState<number>(2026);
  const [tab, setTab] = useState<TabKey>('items');
  const [q, setQ] = useState<string>('');
  const [typeF, setTypeF] = useState<string>('all');
  const [statusF, setStatusF] = useState<string>('all');
  const [filterBranchId, setFilterBranchId] = useState<string>('');
  const [filterDepartmentId, setFilterDepartmentId] = useState<string>('');
  const [showCreate, setShowCreate] = useState<boolean>(false);
  const [payTarget, setPayTarget] = useState<PropertyTaxItem | null>(null);

  const itemsQuery = usePropertyTaxItems(companyId, {
    year,
    ...(filterBranchId ? { branchId: filterBranchId } : {}),
    ...(filterDepartmentId ? { departmentId: filterDepartmentId } : {}),
  });
  const summaryQuery = usePropertyTaxSummary(companyId, year);
  const items: readonly PropertyTaxItem[] = useMemo(() => itemsQuery.data ?? [], [itemsQuery.data]);

  const filtered = useMemo<readonly PropertyTaxItem[]>(
    () =>
      items.filter((r) => {
        if (typeF !== 'all' && r.taxType !== typeF) return false;
        if (statusF !== 'all' && r.derivedStatus !== statusF) return false;
        if (
          q &&
          !(
            r.voucherNo.toLowerCase().includes(q.toLowerCase()) ||
            r.location.toLowerCase().includes(q.toLowerCase())
          )
        )
          return false;
        return true;
      }),
    [items, q, typeF, statusF],
  );

  // KPI counts — derived locally so the cards stay live even before the
  // summary endpoint refetches (cheap; itemsQuery is the source of truth).
  const totals = useMemo(() => {
    const total = items.reduce((s, r) => s + toNum(r.taxAmount), 0);
    const paid = items
      .filter((r) => r.derivedStatus === 'PAID')
      .reduce((s, r) => s + toNum(r.taxAmount), 0);
    const overdueCount = items.filter((r) => r.derivedStatus === 'OVERDUE').length;
    const paidCount = items.filter((r) => r.derivedStatus === 'PAID').length;
    return { total, paid, overdueCount, paidCount };
  }, [items]);

  return (
    <div className="page">
      <PageHead
        crumbs={['รายงานและภาษี', 'ภาษีที่ดิน & ป้าย']}
        title="ภาษีที่ดินและสิ่งปลูกสร้าง + ภาษีป้าย — ทะเบียนรายปี"
        actions={
          <>
            <HelpButton guide="proptax" />
            <select
              className="select"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
            >
              {[2024, 2025, 2026, 2027].map((y) => (
                <option key={y} value={y}>
                  ปีภาษี: {y}
                </option>
              ))}
            </select>
            <button className="btn">
              <Icon name="download" size={14} /> ส่งออก
            </button>
            <button className="btn primary" onClick={() => setShowCreate(true)}>
              <Icon name="plus" size={14} /> ตั้งประมาณการใหม่
            </button>
          </>
        }
      />

      {/* KPI 4-card */}
      <div className="kpi-grid">
        <div className="kpi">
          <div className="label">
            <span className="dot" />
            รายการในทะเบียน
          </div>
          <div className="value tnum">
            {items.length} <span className="unit">รายการ</span>
          </div>
          <div className="trend faint">ปีภาษี {year} · ที่ดิน + ป้าย</div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--brand)' }} />
            ภาษีรวมปี {year}
          </div>
          <div className="value tnum">
            {fmtB(totals.total)} <span className="unit">บาท</span>
          </div>
          <div className="trend faint">มูลค่าประเมินทั้งหมด</div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--positive)' }} />
            จ่ายจริงไปแล้ว
          </div>
          <div className="value tnum">
            {fmtB(totals.paid)} <span className="unit">บาท</span>
          </div>
          <div className="trend pos">
            <Icon name="check" size={11} /> {totals.paidCount}/{items.length} รายการ
          </div>
        </div>
        <div className="kpi">
          <div className="label">
            <span
              className="dot"
              style={{ background: totals.overdueCount ? 'var(--negative)' : 'var(--warning)' }}
            />
            เกินกำหนด
          </div>
          <div className="value tnum">
            {totals.overdueCount} <span className="unit">รายการ</span>
          </div>
          <div className="trend down">
            {totals.overdueCount > 0 ? 'ต้องดำเนินการจ่ายด่วน' : 'ตามแผนปกติ'}
          </div>
        </div>
      </div>

      {/* Workflow explainer banner — V1 full-annual accrual flow */}
      <div
        style={{
          background: '#fafafa',
          border: '1px solid var(--border)',
          borderRadius: 8,
          padding: '14px 18px',
          marginBottom: 'var(--section-gap)',
        }}
      >
        <div className="section-title" style={{ margin: '0 0 10px' }}>
          วงจรการบันทึกบัญชี — V1 ตั้งประมาณการเต็มจำนวน
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
          {[
            {
              n: '1',
              t: 'ลงทะเบียน — ตั้งประมาณการเต็มปี',
              d: 'ระบบสร้างใบสำคัญ accrual เต็มจำนวน',
              acct: 'Dr. 60110114/60110011 / Cr. 20170036/20170035',
              color: '#1a56a6',
            },
            {
              n: '2',
              t: 'จ่ายจริง — เคลียร์ค่าใช้จ่ายค้างจ่าย',
              d: 'เมื่อจ่ายจริงในเดือนกำหนด',
              acct: 'Dr. 20170036/20170035 / Cr. เงินฝากธนาคาร (บัญชีหลักของบริษัท)',
              color: '#117a3d',
            },
            {
              n: '3',
              t: 'รายเดือน — Matrix แสดงการตัดจ่าย',
              d: 'แสดง 1/12 ในแต่ละเดือนปฏิทิน (เชิงข้อมูล)',
              acct: 'ไม่มี JV รายเดือน (V1)',
              color: '#7030A0',
            },
          ].map((s) => (
            <div
              key={s.n}
              style={{
                display: 'flex',
                gap: 10,
                padding: '10px',
                background: 'white',
                borderRadius: 6,
                border: '1px solid var(--border)',
              }}
            >
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  background: s.color,
                  color: 'white',
                  display: 'grid',
                  placeItems: 'center',
                  fontWeight: 700,
                  fontSize: 14,
                  flexShrink: 0,
                  fontFamily: 'var(--font-display)',
                }}
              >
                {s.n}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600 }}>{s.t}</div>
                <div className="meta" style={{ marginTop: 2 }}>
                  {s.d}
                </div>
                <div className="code muted" style={{ fontSize: 13, marginTop: 3 }}>
                  {s.acct}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="page-tabs">
        {(
          [
            ['items', 'รายการประมาณการ'],
            ['matrix', 'ตารางตัดจ่ายรายเดือน (Matrix)'],
            ['summary', 'สรุปตามทรัพย์สิน'],
          ] as readonly (readonly [TabKey, string])[]
        ).map(([k, l]) => (
          <div key={k} className={`pt ${tab === k ? 'active' : ''}`} onClick={() => setTab(k)}>
            {l}
          </div>
        ))}
      </div>

      {tab === 'items' && (
        <ItemsTab
          rows={filtered}
          isLoading={itemsQuery.isLoading}
          q={q}
          setQ={setQ}
          typeF={typeF}
          setTypeF={setTypeF}
          statusF={statusF}
          setStatusF={setStatusF}
          companyId={companyId}
          filterBranchId={filterBranchId}
          setFilterBranchId={setFilterBranchId}
          filterDepartmentId={filterDepartmentId}
          setFilterDepartmentId={setFilterDepartmentId}
          onPay={(r) => setPayTarget(r)}
        />
      )}
      {tab === 'matrix' && (
        <MatrixTab items={items} isLoading={itemsQuery.isLoading} summary={summaryQuery.data} />
      )}
      {tab === 'summary' && <SummaryTab items={items} summary={summaryQuery.data} />}

      {showCreate && (
        <CreateModal companyId={companyId} year={year} onClose={() => setShowCreate(false)} />
      )}
      {payTarget && (
        <PayModal companyId={companyId} item={payTarget} onClose={() => setPayTarget(null)} />
      )}
    </div>
  );
}

// ============================================================
// Items tab — searchable table with status badges + pay button
// ============================================================
function ItemsTab({
  rows,
  isLoading,
  q,
  setQ,
  typeF,
  setTypeF,
  statusF,
  setStatusF,
  companyId,
  filterBranchId,
  setFilterBranchId,
  filterDepartmentId,
  setFilterDepartmentId,
  onPay,
}: {
  rows: readonly PropertyTaxItem[];
  isLoading: boolean;
  q: string;
  setQ: (v: string) => void;
  typeF: string;
  setTypeF: (v: string) => void;
  statusF: string;
  setStatusF: (v: string) => void;
  companyId: string | undefined;
  filterBranchId: string;
  setFilterBranchId: (id: string) => void;
  filterDepartmentId: string;
  setFilterDepartmentId: (id: string) => void;
  onPay: (r: PropertyTaxItem) => void;
}) {
  return (
    <div className="tbl-shell">
      <div className="filter-row">
        <input
          className="input search w-lg"
          placeholder="ค้นหา เลขที่ / สถานที่"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{
            paddingLeft: 32,
            backgroundImage: SEARCH_BG,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: '10px center',
          }}
        />
        <select className="select" value={typeF} onChange={(e) => setTypeF(e.target.value)}>
          <option value="all">ประเภทภาษี: ทั้งหมด</option>
          {TAX_TYPES.map((t) => (
            <option key={t.code} value={t.code}>
              {t.label}
            </option>
          ))}
        </select>
        <select className="select" value={statusF} onChange={(e) => setStatusF(e.target.value)}>
          <option value="all">สถานะ: ทั้งหมด</option>
          <option value="PENDING">รอจ่าย</option>
          <option value="PAID">จ่ายแล้ว</option>
          <option value="OVERDUE">เกินกำหนด</option>
          <option value="CANCELLED">ยกเลิก</option>
        </select>
        <DimensionFilter
          companyId={companyId}
          branchId={filterBranchId}
          departmentId={filterDepartmentId}
          onBranchChange={setFilterBranchId}
          onDepartmentChange={setFilterDepartmentId}
        />
      </div>

      <div className="tbl-wrap" style={{ maxHeight: 540 }}>
        <table className="tbl">
          <thead>
            <tr>
              <th style={{ width: 130 }}>เลขที่</th>
              <th>สถานที่ / ทรัพย์สิน</th>
              <th>ประเภทภาษี</th>
              <th className="num">มูลค่าประเมิน</th>
              <th className="num">อัตราภาษี</th>
              <th className="num">ภาษีต้องจ่าย</th>
              <th>กำหนดจ่าย</th>
              <th>วันจ่ายจริง</th>
              <th>สถานะ</th>
              <th style={{ width: 120 }}>การกระทำ</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={10} className="muted" style={{ padding: 24, textAlign: 'center' }}>
                  กำลังโหลด…
                </td>
              </tr>
            )}
            {!isLoading && rows.length === 0 && (
              <tr>
                <td colSpan={10} className="muted" style={{ padding: 32, textAlign: 'center' }}>
                  ยังไม่มีข้อมูล — กดปุ่ม "<b>+ ตั้งประมาณการใหม่</b>" เพื่อสร้างรายการแรก
                </td>
              </tr>
            )}
            {rows.map((r) => {
              const t = getTaxType(r.taxType);
              const badge = statusBadge(r.derivedStatus);
              const canPay = r.derivedStatus === 'PENDING' || r.derivedStatus === 'OVERDUE';
              return (
                <tr key={r.id}>
                  <td className="code">
                    <a style={{ color: 'var(--brand)', fontWeight: 500 }}>{r.voucherNo}</a>
                  </td>
                  <td>
                    <div style={{ fontWeight: 500 }}>{r.location}</div>
                    {r.branchName && <div className="meta">{r.branchName}</div>}
                  </td>
                  <td>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                      <span style={{ width: 9, height: 9, borderRadius: 2, background: t.tone }} />
                      {t.short}
                    </span>
                  </td>
                  <td className="num tnum">{fmt(toNum(r.appraisedValue))}</td>
                  <td className="num tnum muted">{toNum(r.taxRate).toFixed(2)}%</td>
                  <td className="num tnum">
                    <b>{fmt(toNum(r.taxAmount))}</b>
                  </td>
                  <td>{fmtDate(r.dueDate)}</td>
                  <td>{r.paidDate ? fmtDate(r.paidDate) : <span className="faint">—</span>}</td>
                  <td>
                    <span className={`badge ${badge.c}`}>
                      <span className="bdot" />
                      {badge.l}
                    </span>
                  </td>
                  <td>
                    {canPay && (
                      <button
                        className="btn sm primary"
                        style={{ height: 28, fontSize: 14 }}
                        onClick={() => onPay(r)}
                      >
                        บันทึกจ่าย →
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {rows.length > 0 && (
              <tr className="total">
                <td colSpan={5}>รวม {rows.length} รายการ</td>
                <td className="num tnum">
                  {fmt(rows.reduce((s, r) => s + toNum(r.taxAmount), 0))}
                </td>
                <td colSpan={4} />
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================
// Matrix tab — assets × 12 months (1/12 amortisation visualisation)
// ============================================================
function MatrixTab({
  items,
  isLoading,
  summary,
}: {
  items: readonly PropertyTaxItem[];
  isLoading: boolean;
  summary:
    | {
        byMonth: { month: number; accrualAmount: string; paidAmount: string }[];
      }
    | undefined;
}) {
  if (isLoading)
    return (
      <div className="muted" style={{ padding: 24, textAlign: 'center' }}>
        กำลังโหลด…
      </div>
    );
  if (items.length === 0)
    return (
      <div className="muted" style={{ padding: 32, textAlign: 'center' }}>
        ยังไม่มีข้อมูลสำหรับช่วงเวลานี้
      </div>
    );

  const monthTotals = summary?.byMonth ?? [];

  return (
    <>
      <div className="card" style={{ marginBottom: 'var(--section-gap)' }}>
        <div className="card-head">
          <div>
            <h3>ตารางตัดจ่ายรายเดือน — Matrix</h3>
            <div className="sub">
              ทรัพย์สิน × 12 เดือน · แสดงยอด 1/12 ต่อเดือนพร้อมไฮไลต์เดือนจ่ายจริง
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <span className="chip">
              <span
                style={{
                  width: 10,
                  height: 10,
                  background: '#e8eff8',
                  border: '1px solid #c1d1e8',
                }}
              />{' '}
              ตัดจ่ายปกติ
            </span>
            <span className="chip">
              <span
                style={{
                  width: 10,
                  height: 10,
                  background: '#fdf2e3',
                  border: '2px solid #b54708',
                }}
              />{' '}
              เดือนจ่ายจริง
            </span>
          </div>
        </div>
      </div>

      <div className="tbl-shell">
        <div className="tbl-wrap" style={{ maxHeight: 620, overflowX: 'auto' }}>
          <table className="tbl" style={{ minWidth: 1400 }}>
            <thead>
              <tr>
                <th
                  style={{
                    width: 220,
                    position: 'sticky',
                    left: 0,
                    background: '#fafafa',
                    zIndex: 2,
                  }}
                >
                  สถานที่
                </th>
                <th style={{ width: 80 }}>ประเภท</th>
                <th className="num" style={{ width: 110 }}>
                  ภาษีต้องจ่าย
                </th>
                {MONTHS.map((m) => (
                  <th key={m} className="num" style={{ width: 95 }}>
                    {m}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const t = getTaxType(item.taxType);
                const amt = toNum(item.taxAmount);
                const monthly = amt / 12;
                const paidMonthIdx = item.paidDate ? new Date(item.paidDate).getMonth() : null;
                return (
                  <tr key={item.id}>
                    <td
                      style={{
                        position: 'sticky',
                        left: 0,
                        background: 'white',
                        zIndex: 1,
                        borderRight: '1px solid var(--border)',
                      }}
                    >
                      <div style={{ fontWeight: 500 }}>{item.location}</div>
                      <div className="meta" style={{ marginTop: 1 }}>
                        {item.voucherNo}
                      </div>
                    </td>
                    <td>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <span
                          style={{ width: 8, height: 8, borderRadius: 2, background: t.tone }}
                        />
                        <span style={{ fontSize: 13 }}>{t.short}</span>
                      </span>
                    </td>
                    <td className="num tnum">{fmt(amt)}</td>
                    {MONTHS.map((_, i) => {
                      const isPaid = paidMonthIdx === i;
                      return (
                        <td
                          key={i}
                          className="num tnum"
                          style={{
                            background: isPaid ? '#fdf2e3' : '#e8eff8',
                            color: isPaid ? '#b54708' : '#1a56a6',
                            borderLeft: isPaid ? '3px solid #b54708' : undefined,
                            fontWeight: isPaid ? 700 : 500,
                            fontSize: 14,
                          }}
                        >
                          {fmt(monthly)}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
              <tr className="total">
                <td
                  colSpan={2}
                  style={{ position: 'sticky', left: 0, background: '#f4f4f7', zIndex: 1 }}
                >
                  รวมทุกทรัพย์สิน
                </td>
                <td className="num tnum">
                  {fmt(items.reduce((s, r) => s + toNum(r.taxAmount), 0))}
                </td>
                {MONTHS.map((_, i) => {
                  const bucket = monthTotals.find((b) => b.month === i + 1);
                  const monthSum = bucket
                    ? toNum(bucket.accrualAmount)
                    : items.reduce((s, r) => s + toNum(r.taxAmount) / 12, 0);
                  return (
                    <td key={i} className="num tnum">
                      {fmt(monthSum)}
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

// ============================================================
// Summary tab — by-type cards + by-branch table
// ============================================================
function SummaryTab({
  items,
  summary,
}: {
  items: readonly PropertyTaxItem[];
  summary:
    | {
        byBranch: {
          branchId: string | null;
          branchName: string | null;
          itemCount: number;
          totalTax: string;
          paidAmount: string;
          pendingAmount: string;
        }[];
      }
    | undefined;
}) {
  // By-type aggregation — done client-side so the card always reflects the
  // current filters in the items list (server summary ignores filters).
  const byType = useMemo(() => {
    const out = TAX_TYPES.map((t) => ({
      ...t,
      count: 0,
      total: 0,
      paid: 0,
      pending: 0,
    }));
    for (const r of items) {
      const bucket = out.find((b) => b.code === r.taxType);
      if (!bucket) continue;
      bucket.count += 1;
      bucket.total += toNum(r.taxAmount);
      if (r.derivedStatus === 'PAID') bucket.paid += toNum(r.taxAmount);
      if (r.derivedStatus === 'PENDING' || r.derivedStatus === 'OVERDUE')
        bucket.pending += toNum(r.taxAmount);
    }
    return out;
  }, [items]);

  if (items.length === 0 && (!summary || summary.byBranch.length === 0)) {
    return (
      <div className="muted" style={{ padding: 32, textAlign: 'center' }}>
        ยังไม่มีข้อมูลสำหรับช่วงเวลานี้
      </div>
    );
  }

  return (
    <>
      <div className="grid-2-eq" style={{ marginBottom: 'var(--section-gap)' }}>
        {byType.map((t) => {
          const paidPct = t.total > 0 ? Math.round((t.paid / t.total) * 100) : 0;
          return (
            <div className="card" key={t.code}>
              <div className="card-head">
                <div>
                  <h3>
                    <span
                      style={{
                        display: 'inline-block',
                        width: 11,
                        height: 11,
                        borderRadius: 2,
                        background: t.tone,
                        marginRight: 8,
                        verticalAlign: 'middle',
                      }}
                    />
                    {t.label}
                  </h3>
                  <div className="sub">
                    บัญชี {t.acct} · {t.count} ทรัพย์สิน · {t.desc}
                  </div>
                </div>
              </div>
              <div className="card-body">
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 14 }}>
                  <div>
                    <div className="meta">ภาษีรวมทั้งปี</div>
                    <div
                      style={{
                        fontSize: 22,
                        fontWeight: 600,
                        marginTop: 4,
                        fontFamily: 'var(--font-display)',
                      }}
                      className="tnum"
                    >
                      {fmt(t.total)}
                    </div>
                  </div>
                  <div>
                    <div className="meta">จ่ายแล้ว ({paidPct}%)</div>
                    <div
                      style={{
                        fontSize: 22,
                        fontWeight: 600,
                        marginTop: 4,
                        color: 'var(--positive)',
                        fontFamily: 'var(--font-display)',
                      }}
                      className="tnum"
                    >
                      {fmt(t.paid)}
                    </div>
                  </div>
                  <div>
                    <div className="meta">ค้างจ่าย</div>
                    <div
                      style={{
                        fontSize: 18,
                        fontWeight: 600,
                        marginTop: 4,
                        color: 'var(--warning)',
                        fontFamily: 'var(--font-display)',
                      }}
                      className="tnum"
                    >
                      {fmt(t.pending)}
                    </div>
                  </div>
                  <div>
                    <div className="meta">ตัดจ่าย/เดือน (1/12)</div>
                    <div
                      style={{
                        fontSize: 18,
                        fontWeight: 600,
                        marginTop: 4,
                        color: 'var(--brand)',
                        fontFamily: 'var(--font-display)',
                      }}
                      className="tnum"
                    >
                      {fmt(t.total / 12)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* By-branch breakdown — fed from server summary so it covers branches
          that may not be visible in the current items filter. */}
      {summary && summary.byBranch.length > 0 && (
        <div className="tbl-shell">
          <div className="filter-row">
            <span style={{ fontSize: 14, fontWeight: 600 }}>สรุปตามสาขา</span>
          </div>
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>สาขา</th>
                  <th className="num">จำนวนรายการ</th>
                  <th className="num">ภาษีรวม</th>
                  <th className="num">จ่ายแล้ว</th>
                  <th className="num">ค้างจ่าย</th>
                </tr>
              </thead>
              <tbody>
                {summary.byBranch.map((b) => (
                  <tr key={b.branchId ?? '__none__'}>
                    <td>{b.branchName ?? <span className="faint">— ไม่ระบุสาขา —</span>}</td>
                    <td className="num tnum">{b.itemCount}</td>
                    <td className="num tnum">
                      <b>{fmt(toNum(b.totalTax))}</b>
                    </td>
                    <td className="num tnum pos">{fmt(toNum(b.paidAmount))}</td>
                    <td className="num tnum">{fmt(toNum(b.pendingAmount))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}

// ============================================================
// Create modal — register a new annual filing (posts the accrual JV)
// ============================================================
function CreateModal({
  companyId,
  year,
  onClose,
}: {
  companyId: string | undefined;
  year: number;
  onClose: () => void;
}) {
  const branchesQuery = useBranches(companyId);
  const createMut = useCreatePropertyTaxItem(companyId);
  const [taxType, setTaxType] = useState<PropertyTaxType>('LAND_BUILDING');
  const [branchId, setBranchId] = useState<string>('');
  const [departmentId, setDepartmentId] = useState<string>('');
  const [location, setLocation] = useState<string>('');
  const [appraisedValue, setAppraisedValue] = useState<string>('');
  const [taxRate, setTaxRate] = useState<string>('0.30');
  const [taxAmount, setTaxAmount] = useState<string>('');
  const [dueDate, setDueDate] = useState<string>(`${year}-04-30`);
  const [notes, setNotes] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  function submit() {
    setError(null);
    const payload: CreatePropertyTaxItem = {
      taxType,
      year,
      ...(branchId ? { branchId } : {}),
      ...(departmentId ? { departmentId } : {}),
      location,
      appraisedValue: Number(appraisedValue) || 0,
      taxRate: Number(taxRate) || 0,
      taxAmount: Number(taxAmount) || 0,
      dueDate,
      ...(notes ? { notes } : {}),
    };
    createMut.mutate(
      { data: payload },
      {
        onSuccess: () => onClose(),
        onError: (err: unknown) => {
          setError(err instanceof Error ? err.message : 'เกิดข้อผิดพลาด');
        },
      },
    );
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,15,20,0.45)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 100,
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 12,
          width: 720,
          maxWidth: '100%',
          boxShadow: '0 28px 80px rgba(0,0,0,0.18)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '18px 22px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <h3
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 600,
                fontFamily: 'var(--font-display)',
              }}
            >
              ตั้งประมาณการภาษีรายการใหม่ ปี {year}
            </h3>
            <div className="meta" style={{ marginTop: 2 }}>
              ระบบจะออกเลข PT-{year}-NNNNNN และโพสต์ JV accrual อัตโนมัติ
            </div>
          </div>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>
        <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label className="meta" style={{ fontWeight: 500 }}>
              ประเภทภาษี
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 6 }}>
              {TAX_TYPES.map((t) => (
                <div
                  key={t.code}
                  onClick={() => {
                    setTaxType(t.code);
                    setDueDate(`${year}-${t.code === 'SIGN' ? '03-31' : '04-30'}`);
                  }}
                  style={{
                    border: `2px solid ${taxType === t.code ? t.tone : 'var(--border)'}`,
                    borderRadius: 8,
                    padding: 12,
                    cursor: 'pointer',
                    background: taxType === t.code ? '#fafafa' : 'white',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 2, background: t.tone }} />
                    <span style={{ fontSize: 15, fontWeight: 600 }}>{t.label}</span>
                  </div>
                  <div className="meta" style={{ marginTop: 4 }}>
                    {t.desc}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <label className="meta" style={{ fontWeight: 500 }}>
              สาขา (ถ้ามี)
            </label>
            <select
              className="select"
              value={branchId}
              onChange={(e) => setBranchId(e.target.value)}
              style={{ width: '100%', height: 38, fontSize: 14, marginTop: 6 }}
            >
              <option value="">— ไม่ระบุสาขา —</option>
              {(branchesQuery.data ?? []).map((b) => (
                <option key={b.id} value={b.id}>
                  {b.nameTh}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="meta" style={{ fontWeight: 500 }}>
              หน่วยงาน (ถ้ามี)
            </label>
            <DepartmentSelect
              companyId={companyId}
              value={departmentId}
              onChange={setDepartmentId}
              branchId={branchId}
              style={{ width: '100%', height: 38, fontSize: 14, marginTop: 6 }}
            />
          </div>
          <div>
            <label className="meta" style={{ fontWeight: 500 }}>
              สถานที่ / ทรัพย์สิน
            </label>
            <input
              className="input"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="เช่น โรงงาน 1 สระบุรี ต.พุแค"
              style={{ width: '100%', height: 38, fontSize: 14, marginTop: 6 }}
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
            <div>
              <label className="meta" style={{ fontWeight: 500 }}>
                มูลค่าประเมิน
              </label>
              <input
                className="input tnum"
                type="number"
                value={appraisedValue}
                onChange={(e) => setAppraisedValue(e.target.value)}
                style={{
                  width: '100%',
                  height: 38,
                  fontSize: 14,
                  textAlign: 'right',
                  marginTop: 6,
                }}
              />
            </div>
            <div>
              <label className="meta" style={{ fontWeight: 500 }}>
                อัตราภาษี (%)
              </label>
              <input
                className="input tnum"
                type="number"
                step="0.01"
                value={taxRate}
                onChange={(e) => setTaxRate(e.target.value)}
                style={{
                  width: '100%',
                  height: 38,
                  fontSize: 14,
                  textAlign: 'right',
                  marginTop: 6,
                }}
              />
            </div>
            <div>
              <label className="meta" style={{ fontWeight: 500 }}>
                ภาษีต้องจ่าย
              </label>
              <input
                className="input tnum"
                type="number"
                value={taxAmount}
                onChange={(e) => setTaxAmount(e.target.value)}
                style={{
                  width: '100%',
                  height: 38,
                  fontSize: 14,
                  textAlign: 'right',
                  fontWeight: 600,
                  marginTop: 6,
                }}
              />
            </div>
          </div>
          <div>
            <label className="meta" style={{ fontWeight: 500 }}>
              กำหนดจ่าย
            </label>
            <input
              className="input"
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              style={{ width: '100%', height: 38, fontSize: 14, marginTop: 6 }}
            />
          </div>
          <div>
            <label className="meta" style={{ fontWeight: 500 }}>
              หมายเหตุ
            </label>
            <input
              className="input"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="(ไม่บังคับ)"
              style={{ width: '100%', height: 38, fontSize: 14, marginTop: 6 }}
            />
          </div>
          {error && (
            <div
              style={{
                padding: '10px 12px',
                background: '#fce9e7',
                border: '1px solid #f3b1ad',
                borderRadius: 6,
                color: 'var(--negative)',
                fontSize: 13,
              }}
            >
              {error}
            </div>
          )}
        </div>
        <div
          style={{
            padding: '14px 22px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            background: '#fafafa',
          }}
        >
          <button className="btn ghost" onClick={onClose} disabled={createMut.isPending}>
            ยกเลิก
          </button>
          <button
            className="btn primary"
            onClick={submit}
            disabled={createMut.isPending || !location || !taxAmount || Number(taxAmount) <= 0}
          >
            <Icon name="check" size={14} />{' '}
            {createMut.isPending ? 'กำลังบันทึก…' : 'สร้าง + โพสต์ JV accrual'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Pay modal — record payment date + cash account → posts payment JV
// ============================================================
function PayModal({
  companyId,
  item,
  onClose,
}: {
  companyId: string | undefined;
  item: PropertyTaxItem;
  onClose: () => void;
}) {
  const payMut = usePayPropertyTaxItem(companyId);
  const [paidDate, setPaidDate] = useState<string>(new Date().toISOString().slice(0, 10));
  // Empty = let the backend resolve BANK_PRIMARY (the company's configured default
  // cash account, set in /settings/account-mapping). Optionally override per-payment.
  const [cashAccountCode, setCashAccountCode] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const t = getTaxType(item.taxType);

  function submit() {
    setError(null);
    payMut.mutate(
      {
        id: item.id,
        data: {
          paidDate,
          ...(cashAccountCode.trim() ? { cashAccountCode: cashAccountCode.trim() } : {}),
        },
      },
      {
        onSuccess: () => onClose(),
        onError: (err: unknown) => {
          setError(err instanceof Error ? err.message : 'เกิดข้อผิดพลาด');
        },
      },
    );
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,15,20,0.45)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 100,
        padding: 20,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 12,
          width: 560,
          maxWidth: '100%',
          boxShadow: '0 28px 80px rgba(0,0,0,0.18)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '18px 22px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <h3
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 600,
                fontFamily: 'var(--font-display)',
              }}
            >
              บันทึกการจ่าย — <span style={{ color: t.tone }}>{t.label}</span>
            </h3>
            <div className="meta" style={{ marginTop: 2 }}>
              <b style={{ color: '#3f3f46' }}>{item.voucherNo}</b> · {item.location} · ภาษี{' '}
              {fmt(toNum(item.taxAmount))} บาท
            </div>
          </div>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>
        <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label className="meta" style={{ fontWeight: 500 }}>
              วันที่จ่าย
            </label>
            <input
              className="input"
              type="date"
              value={paidDate}
              onChange={(e) => setPaidDate(e.target.value)}
              style={{ width: '100%', height: 38, fontSize: 14, marginTop: 6 }}
            />
          </div>
          <div>
            <label className="meta" style={{ fontWeight: 500 }}>
              บัญชีเงินสด/ธนาคาร (Cr.) — เว้นว่างได้
            </label>
            <input
              className="input"
              value={cashAccountCode}
              onChange={(e) => setCashAccountCode(e.target.value)}
              placeholder="เว้นว่าง = บัญชีธนาคารหลัก (ตามตั้งค่าบัญชีอ้างอิง)"
              style={{ width: '100%', height: 38, fontSize: 14, marginTop: 6 }}
            />
            <div className="meta" style={{ marginTop: 6 }}>
              JV: Dr. {t.accrued} ค่าใช้จ่ายค้างจ่าย / Cr.{' '}
              {cashAccountCode || 'เงินฝากธนาคารหลัก (ตามตั้งค่า)'}
            </div>
          </div>
          {error && (
            <div
              style={{
                padding: '10px 12px',
                background: '#fce9e7',
                border: '1px solid #f3b1ad',
                borderRadius: 6,
                color: 'var(--negative)',
                fontSize: 13,
              }}
            >
              {error}
            </div>
          )}
        </div>
        <div
          style={{
            padding: '14px 22px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            background: '#fafafa',
          }}
        >
          <button className="btn ghost" onClick={onClose} disabled={payMut.isPending}>
            ยกเลิก
          </button>
          <button className="btn primary" onClick={submit} disabled={payMut.isPending || !paidDate}>
            <Icon name="check" size={14} />{' '}
            {payMut.isPending ? 'กำลังบันทึก…' : 'ยืนยัน & สร้าง JV จ่าย'}
          </button>
        </div>
      </div>
    </div>
  );
}
